import { useEffect, useState } from 'react';
import { PhaseTypeEnum } from '@vatos-pas/common';
import { isTrimOptionPhase } from '../../constants/phaseConfigurations';
import type { PhaseOptionControl } from './PhaseOptionsSection';

interface TrimOptionPhase {
  id: string;
  phaseType: PhaseTypeEnum;
  trimOptionSelected?: boolean | null;
}

interface TrimOptionJob {
  hasTrimOptions?: boolean | null;
  trimOptionsFinished?: boolean | null;
}

interface UseTrimOptionSelectionParams {
  open: boolean;
  phase: TrimOptionPhase;
  job?: TrimOptionJob | null;
}

export interface TrimOptionSelection {
  show: boolean;
  finished: boolean;
  isTrimTwo: boolean;
  // What the modal submits: a finished option stays selected regardless of
  // the local checkbox.
  selected: boolean;
  setSelected: (selected: boolean) => void;
  control: PhaseOptionControl | null;
}

// The selection is pre-filled from the phase and finalized by the backend on
// approval, so the modal never persists it to the job itself.
export function useTrimOptionSelection({
  open,
  phase,
  job,
}: UseTrimOptionSelectionParams): TrimOptionSelection {
  const show = Boolean(
    job?.hasTrimOptions && isTrimOptionPhase(phase.phaseType),
  );
  const finished = Boolean(job?.trimOptionsFinished);
  const [localSelected, setLocalSelected] = useState(false);

  useEffect(() => {
    if (!open || !show) return;

    setLocalSelected(Boolean(phase.trimOptionSelected));
  }, [open, phase.id, phase.trimOptionSelected, show]);

  const selected = finished || localSelected;

  return {
    show,
    finished,
    isTrimTwo: show && phase.phaseType === PhaseTypeEnum.Trim2,
    selected,
    setSelected: setLocalSelected,
    control: show
      ? {
          label: 'Trim Option',
          checked: selected,
          onChange: setLocalSelected,
          disabled: finished,
        }
      : null,
  };
}
